import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMessage } from "../../lib/api";
import { formatDate, t } from "../../lib/i18n";
import { Loader, EmptyState } from "../../components/Blocks";

export function AdminChallengeEntries() {
  const [items, setItems] = useState(null);
  const [names, setNames] = useState({ challenges: {}, profiles: {} });

  const load = async () => {
    const [entries, challenges, profiles] = await Promise.all([
      api.get("/admin/challenge-entries"),
      api.get("/admin/challenges"),
      api.get("/admin/profiles"),
    ]);
    setItems(entries.data);
    setNames({
      challenges: Object.fromEntries(challenges.data.map((c) => [c.id, c.title])),
      profiles: Object.fromEntries(profiles.data.map((p) => [p.id, p.display_name])),
    });
  };
  useEffect(() => {
    load().catch((e) => toast.error(errMessage(e)));
  }, []);

  const review = async (id, status) => {
    try {
      await api.patch(`/admin/challenge-entries/${id}`, { status });
      toast.success(`Entrée : ${t(`statuses.${status}`, status)}`);
      load();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  if (items === null) return <Loader />;

  return (
    <div data-testid="admin-challenge-entries">
      <h1 className="vb-display text-3xl lg:text-4xl font-bold">Entrées de challenge</h1>
      <p className="mt-3 text-sm text-neutral-500">
        Vérification manuelle des preuves. Aucun scraping des réseaux sociaux, aucun moteur de score.
      </p>
      {items.length === 0 ? (
        <div className="mt-8">
          <EmptyState message="Aucune entrée." testid="admin-entries-empty" />
        </div>
      ) : (
        <div className="mt-8 overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="admin-entries-table">
            <thead>
              <tr className="text-left vb-label border-b border-white/10">
                <th className="py-3 pr-4">Challenge</th>
                <th className="py-3 pr-4">Profil</th>
                <th className="py-3 pr-4">Preuve</th>
                <th className="py-3 pr-4">Interactions</th>
                <th className="py-3 pr-4">Statut</th>
                <th className="py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-b border-white/5" data-testid={`admin-entry-row-${e.id}`}>
                  <td className="py-3 pr-4 text-xs text-neutral-300">
                    {names.challenges[e.challenge_id] || e.challenge_id}
                  </td>
                  <td className="py-3 pr-4 text-xs text-neutral-300">
                    {names.profiles[e.profile_id] || e.profile_id}
                  </td>
                  <td className="py-3 pr-4 text-xs">
                    {e.social_post_url ? (
                      <a href={e.social_post_url} target="_blank" rel="noopener noreferrer" className="text-[#FF2E63] hover:underline">
                        Publication
                      </a>
                    ) : (
                      e.media_id || "—"
                    )}
                  </td>
                  <td className="py-3 pr-4">{e.manually_entered_engagement_count ?? "—"}</td>
                  <td className="py-3 pr-4">
                    <span className="text-xs px-2 py-1 border border-white/10">{t(`statuses.${e.status}`, e.status)}</span>
                  </td>
                  <td className="py-3 whitespace-nowrap">
                    <button className="vb-btn vb-btn-ghost !py-1 !px-3 !text-xs" data-testid={`admin-entry-verify-${e.id}`} onClick={() => review(e.id, "verified")}>
                      Vérifier
                    </button>
                    <button className="vb-btn vb-btn-primary !py-1 !px-3 !text-xs ml-2" data-testid={`admin-entry-winner-${e.id}`} onClick={() => review(e.id, "winner")}>
                      Gagnant
                    </button>
                    <button className="ml-3 text-xs text-[#FF3B30] hover:underline" data-testid={`admin-entry-reject-${e.id}`} onClick={() => review(e.id, "rejected")}>
                      Rejeter
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AdminConsents() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ person_id: "", allowed_usage: "vytaliboots", event_id: "", notes: "" });

  const load = async () => setItems((await api.get("/admin/consents")).data);
  useEffect(() => {
    load().catch((e) => toast.error(errMessage(e)));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/consents", {
        person_id: form.person_id,
        allowed_usage: form.allowed_usage.split(",").map((s) => s.trim()).filter(Boolean),
        event_id: form.event_id || null,
        notes: form.notes || null,
      });
      toast.success("Consentement créé");
      setForm({ person_id: "", allowed_usage: "vytaliboots", event_id: "", notes: "" });
      load();
    } catch (err) {
      toast.error(errMessage(err));
    }
  };

  const revoke = async (id) => {
    try {
      await api.post(`/admin/consents/${id}/revoke`);
      toast.success("Consentement révoqué");
      load();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  if (items === null) return <Loader />;

  return (
    <div data-testid="admin-consents">
      <h1 className="vb-display text-3xl lg:text-4xl font-bold">Consentements média</h1>
      <p className="mt-3 text-sm text-neutral-500 max-w-3xl">
        Un consentement n'est jamais permanent et ne couvre pas automatiquement tous les usages
        futurs. Les registres de consentement ne sont jamais exposés publiquement.
      </p>

      <form onSubmit={create} className="mt-8 vb-card p-6 grid md:grid-cols-4 gap-6" data-testid="admin-consent-form">
        <div>
          <label className="vb-label">ID de la personne (profil)</label>
          <input className="vb-input" required value={form.person_id} onChange={(e) => setForm({ ...form, person_id: e.target.value })} data-testid="admin-consent-person" />
        </div>
        <div>
          <label className="vb-label">Usages autorisés</label>
          <input className="vb-input" value={form.allowed_usage} onChange={(e) => setForm({ ...form, allowed_usage: e.target.value })} data-testid="admin-consent-usage" />
        </div>
        <div>
          <label className="vb-label">ID événement (optionnel)</label>
          <input className="vb-input" value={form.event_id} onChange={(e) => setForm({ ...form, event_id: e.target.value })} data-testid="admin-consent-event" />
        </div>
        <div className="flex items-end">
          <button type="submit" className="vb-btn vb-btn-primary w-full" data-testid="admin-consent-create">
            Créer
          </button>
        </div>
      </form>

      {items.length === 0 ? (
        <div className="mt-8">
          <EmptyState message="Aucun consentement." testid="admin-consents-empty" />
        </div>
      ) : (
        <div className="mt-8 overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="admin-consents-table">
            <thead>
              <tr className="text-left vb-label border-b border-white/10">
                <th className="py-3 pr-4">Personne</th>
                <th className="py-3 pr-4">Type</th>
                <th className="py-3 pr-4">Version</th>
                <th className="py-3 pr-4">Usages</th>
                <th className="py-3 pr-4">Accepté</th>
                <th className="py-3 pr-4">Révoqué</th>
                <th className="py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-white/5" data-testid={`admin-consent-row-${c.id}`}>
                  <td className="py-3 pr-4 text-xs text-neutral-400">{c.person_id}</td>
                  <td className="py-3 pr-4 text-xs">{c.consent_type}</td>
                  <td className="py-3 pr-4 text-xs">{c.consent_version}</td>
                  <td className="py-3 pr-4 text-xs">{(c.allowed_usage || []).join(", ")}</td>
                  <td className="py-3 pr-4 text-xs text-neutral-500">{formatDate(c.accepted_at)}</td>
                  <td className="py-3 pr-4 text-xs">{c.revoked ? "Oui" : "Non"}</td>
                  <td className="py-3">
                    {!c.revoked && (
                      <button className="text-xs text-[#FF3B30] hover:underline" data-testid={`admin-consent-revoke-${c.id}`} onClick={() => revoke(c.id)}>
                        Révoquer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AdminAudit() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/admin/audit").then(({ data }) => setItems(data)).catch((e) => toast.error(errMessage(e)));
  }, []);
  if (items === null) return <Loader />;
  return (
    <div data-testid="admin-audit">
      <h1 className="vb-display text-3xl lg:text-4xl font-bold">Audit</h1>
      <p className="mt-3 text-sm text-neutral-500">Actions privilégiées journalisées, sans données sensibles.</p>
      {items.length === 0 ? (
        <div className="mt-8">
          <EmptyState message="Aucune entrée d'audit." testid="admin-audit-empty" />
        </div>
      ) : (
        <div className="mt-8 overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]" data-testid="admin-audit-table">
            <thead>
              <tr className="text-left vb-label border-b border-white/10">
                <th className="py-3 pr-4">Action</th>
                <th className="py-3 pr-4">Entité</th>
                <th className="py-3 pr-4">ID entité</th>
                <th className="py-3 pr-4">Acteur</th>
                <th className="py-3 pr-4">Horodatage</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-b border-white/5" data-testid={`admin-audit-row-${a.id}`}>
                  <td className="py-3 pr-4">{a.action}</td>
                  <td className="py-3 pr-4 text-neutral-400">{a.entity_type}</td>
                  <td className="py-3 pr-4 text-xs text-neutral-600">{a.entity_id}</td>
                  <td className="py-3 pr-4 text-xs text-neutral-600">{a.actor_id}</td>
                  <td className="py-3 pr-4 text-xs text-neutral-500">{formatDate(a.timestamp || a.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
